import { useCallback, useEffect, useState } from 'react';
import { useHistory, generatePath } from 'react-router-dom';
import { parseLocation } from 'automation-ui-routing/utils';
import useCollabGenerateActions from 'automation-ui-canvas/copilot-collaboration/hooks/useCollabGenerateActions';
import { transformFlowWithAppendedActions } from 'automation-ui-canvas/utils/aiAssistantUtils';
import { useSelector, useDispatch } from 'react-redux';
import { updateFlowLocally } from 'automation-ui-canvas/actions/FlowActions';
import * as SourceApps from 'automation-ui-source-apps/constants/SourceApps';
import { useUpdateFlow } from 'automation-ui-canvas/queries/flowQueries';
import { useCloseCopilot } from 'copilot-toolkit/hooks/useCloseCopilot';
import { addUserEditedActionId } from 'automation-ui-canvas/actions/FlowActions';
import { basePaths } from 'automation-ui-canvas/constants/Routes';
import { getFlowRoute } from 'automation-ui-routing/utils';
import { CANVAS_ROOT } from 'automation-ui-canvas/constants/AbsolutePaths';
import { NEW_ACTION } from 'automation-ui-flow-core/canvas/SpecialActionIds';
import { setScrollToActionCardState } from 'automation-ui-canvas/utils/routingUtils';
import { getActionsPrompt } from 'automation-ui-canvas/selectors/aiAssistantSelectors';
import { generateActionCardPropsById } from 'automation-ui-canvas/containers/flow-views/collab-gen-actions-flow/utils/generateActionCardPropsById';
import { useTrackEvent } from 'automation-ui-usage-tracking/usageTrackingHooks';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import { useSendOnCopilotChannel } from 'copilot-toolkit/channel/hooks/useSendOnCopilotChannel';
import { TOPIC_NAMES } from 'copilot-toolkit/types/Channel';
const useGetCollabGenActionsFlowData = ({
  flow
}) => {
  var _stagedFlowInfo$gener, _stagedFlowInfo$gener2;
  const trackEvent = useTrackEvent();
  const history = useHistory();
  const dispatch = useDispatch();
  const closeCopilot = useCloseCopilot();
  const [updateFlowMutation] = useUpdateFlow();
  const edgeId = parseLocation(history.location).edgeId;
  const initialPrompt = useSelector(getActionsPrompt);
  // The original flow is used as the base to preview and save the generated actions to.
  // This is because the flow gets modified as the user adds or previews actions to the flow during collaboration.
  const [originalFlow] = useState(flow);
  const [stagedFlowInfo, setStagedFlowInfo] = useState(null);
  const [lastMessageQuery, setLastMessageQuery] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const promptUserForMoreInfo = useSendOnCopilotChannel(TOPIC_NAMES.PROMPT_USER_FOR_MORE_INFO);
  const exitFlowViewFn = useCallback(() => {
    setStagedFlowInfo(null);
    dispatch(updateFlowLocally(originalFlow.removeAction(NEW_ACTION)));
    history.push({
      pathname: generatePath(CANVAS_ROOT, {
        screen: 'edit',
        flowId: originalFlow.getFlowId()
      }),
      search: ''
    });
  }, [dispatch, history, originalFlow]);
  const saveFlowAndExitFlowViewFn = useCallback(flowToSaveInfo => {
    if (!flowToSaveInfo) {
      return;
    }
    setIsSaving(true);
    updateFlowMutation({
      variables: {
        flow: flowToSaveInfo.flow,
        sourceApp: SourceApps.WORKFLOWS_APP
      }
    }).then(() => {
      setStagedFlowInfo(null);
      closeCopilot();
      dispatch(addUserEditedActionId({
        flow: flowToSaveInfo.flow,
        action: flowToSaveInfo.flow.getActionById(flowToSaveInfo.lastAddedActionId)
      }));
      history.push({
        pathname: `${getFlowRoute(flowToSaveInfo.flow.getFlowId())}/${basePaths.EDIT}`,
        search: '',
        state: {
          scrollToAction: {
            actionId: flowToSaveInfo.lastAddedActionId,
            scrollBehavior: 'smooth'
          }
        }
      });
    }).catch(err => {
      reportError({
        error: err
      });
      setIsSaving(false);
    }).finally(() => {
      setIsSaving(false);
    });
  }, [updateFlowMutation, closeCopilot, dispatch, history]);
  const trackSaveFlowEvent = useCallback(messageQuery => {
    if (!messageQuery) {
      return;
    }
    trackEvent(Trackables.INSERT_GENERATED_ASSET, {
      aiPrompt: messageQuery === null || messageQuery === void 0 ? void 0 : messageQuery.userInput,
      assetType: 'AI Generated Workflow Action',
      flowId: originalFlow.getFlowId()
    });
  }, [trackEvent, originalFlow]);

  // This is the function that is called when the user clicks the "Save changes"
  // button in the flowView exit confirm modal. It uses the staged flow info to save
  // the flow and exit the flow view.
  const saveStagedFlowAndExitFlowViewFn = useCallback(() => {
    if (!stagedFlowInfo) {
      return;
    }
    trackSaveFlowEvent(lastMessageQuery);
    saveFlowAndExitFlowViewFn({
      flow: stagedFlowInfo.flow,
      lastAddedActionId: stagedFlowInfo.lastAddedActionId
    });
  }, [stagedFlowInfo, trackSaveFlowEvent, lastMessageQuery, saveFlowAndExitFlowViewFn]);
  const onGeneratedActionsPreview = useCallback(({
    toolOutput,
    messageQuery
  }) => {
    const {
      firstActionId,
      actions
    } = toolOutput;
    trackEvent(Trackables.CLICK_TO_GENERATE_ASSET, {
      action: 'previewed generated asset',
      aiPrompt: messageQuery === null || messageQuery === void 0 ? void 0 : messageQuery.userInput,
      assetType: 'AI Generated Workflow Action',
      flowId: originalFlow.getFlowId(),
      destination: edgeId
    });
    const flowToStage = transformFlowWithAppendedActions({
      firstActionId,
      actions,
      edgeId,
      originalFlow: originalFlow.removeAction(NEW_ACTION)
    });
    setLastMessageQuery(messageQuery);
    setStagedFlowInfo({
      flow: flowToStage,
      lastAddedActionId: firstActionId,
      generatedActions: actions
    });
    dispatch(updateFlowLocally(flowToStage));
    setScrollToActionCardState(firstActionId, history);
  }, [edgeId, dispatch, originalFlow, history, trackEvent]);
  const onGeneratedActionsApply = useCallback(({
    toolOutput,
    messageQuery
  }) => {
    trackEvent(Trackables.INSERT_GENERATED_ASSET, {
      aiPrompt: messageQuery === null || messageQuery === void 0 ? void 0 : messageQuery.userInput,
      assetType: 'AI Generated Workflow Action',
      flowId: originalFlow.getFlowId()
    });
    const {
      firstActionId,
      actions
    } = toolOutput;
    const flowWithAppendedActions = transformFlowWithAppendedActions({
      firstActionId: firstActionId !== null && firstActionId !== void 0 ? firstActionId : originalFlow === null || originalFlow === void 0 ? void 0 : originalFlow.getNextAvailableActionId(),
      actions,
      edgeId,
      originalFlow: originalFlow.removeAction(NEW_ACTION)
    });
    const lastAction = Object.values(actions).at(-1);
    const lastAddedActionId = lastAction === null || lastAction === void 0 ? void 0 : lastAction.getActionId();
    if (!lastAddedActionId) {
      reportError({
        error: 'No last added action id found'
      });
      setIsSaving(false);
      return;
    }
    saveFlowAndExitFlowViewFn({
      flow: flowWithAppendedActions,
      lastAddedActionId
    });
  }, [edgeId, originalFlow, saveFlowAndExitFlowViewFn, trackEvent]);
  const {
    sendActionFromAppToCopilot,
    isCopilotReady
  } = useCollabGenerateActions({
    onPreview: onGeneratedActionsPreview,
    onApply: onGeneratedActionsApply,
    flowId: flow.getFlowId(),
    sessionId: `workflows-collaborate-ui-${originalFlow.getFlowId()}-${edgeId}-${originalFlow.getNextAvailableActionId()}`
  });
  useEffect(() => {
    return () => {
      // Reset copilot's input state when unmounting
      // Prevents copilot from remaining in an input-waiting state after the canvas view changes
      // @ts-expect-error This is a valid use case for nulling the payload
      promptUserForMoreInfo(null);
      exitFlowViewFn();
      closeCopilot();
    };
  }, [closeCopilot, exitFlowViewFn, promptUserForMoreInfo]);
  const actionCardPropsById = generateActionCardPropsById({
    originalFlowActionIds: originalFlow.getActions().toArray().map(action => action.getActionId()),
    generatedActionIds: (_stagedFlowInfo$gener = stagedFlowInfo === null || stagedFlowInfo === void 0 || (_stagedFlowInfo$gener2 = stagedFlowInfo.generatedActions) === null || _stagedFlowInfo$gener2 === void 0 ? void 0 : _stagedFlowInfo$gener2.map(action => action.getActionId())) !== null && _stagedFlowInfo$gener !== void 0 ? _stagedFlowInfo$gener : []
  });
  return {
    actionCardPropsById,
    edgeId,
    initialPrompt,
    isCopilotReady,
    isSaving,
    sendActionFromAppToCopilot,
    confirmModal: {
      onSave: saveStagedFlowAndExitFlowViewFn,
      onCancel: exitFlowViewFn,
      disableConfirmModal: !stagedFlowInfo
    }
  };
};
export default useGetCollabGenActionsFlowData;